import styled from "@emotion/styled"
import { useRef } from "react"
import { motion, useMotionTemplate, useScroll, useTransform, transform } from "framer-motion"

import Video, { cssContainer } from "components/media/VideoCLD"
import VideoLauncher from "components/video-launcher/VideoLauncher"

import { getGradient } from "components/articles/common"

import PageTitleBlock from "./PageTitleBlock"
import BlockCTA from "./BlockCTA"
import Skyline from "./Skyline"
import SubHeading from "./SubHeading"
import Overlay from "./Overlay"
import { sectionStyle } from "./style"
import WatermarkOyster from "components/oyster-story-logo/Watermark"
import { cssVars } from "components/nav-v2/Wrapper"

const Section = styled.section`
  ${sectionStyle}
  margin-block-start: calc(var(--subnav-height) / 2);
  background: white; // TODO: get from theme?

  & > svg {
    position: sticky;
    grid-column: doc;
    grid-row: 1;
    height: calc(94vh - var(${cssVars.main.safe}));
    margin-inline-start: 2vh;
    align-self: start;
    margin-block-start: var(${cssVars.main.safe});
    margin-block-start: calc(var(${cssVars.main.safe}) * -1);
    padding-block-end: 3vh;
    padding-block-end: 15vh;
    top: calc(var(${cssVars.main.safe}) + 3vh);
  }
`

const BackgroundMask = styled(motion.div)`
  position: sticky;
  top: 0;
  width: 100%;
  height: 100vh;
  grid-column: doc;
  grid-row: 1;
  z-index: -1;
  overflow: hidden;

  .${cssContainer} {
    width: 100%;
    height: 100%;
  }

  .${cssContainer} img,
  .${cssContainer} video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const VideoStage = styled.section`
  grid-column: doc;
  grid-row: video-start / video-end;
  position: relative;
  min-height: var(--video-row-h, 0px);
  z-index: 1;
`

const LauncherLayer = styled(motion.div)`
  grid-column: doc;
  position: sticky;
  top: 0;
  height: 85vh;
  display: grid;
  place-items: center;
  z-index: 1;

  & > * {
    margin-block-start: 15vh;
  }
`
//
export default function CoverIntroVideo({
  heading,
  video_cld: video,
  intro,
  video_amp_cta: video_cta,
  sub_heading,
  title_gradient,
  chapo_gradient,
  className,
  theme,
  secundary_theme,
  use_os_logo,
  animated_logo,
  logo_translation,
  watermark,
  has_long_sub_heading,
  ...props
}) {
  const blocks = intro?.reduce((acc, item) => {
    for (const [key, value] of Object.entries(item)) {
      acc.set(key, value)
    }
    return acc
  }, new Map())

  const ref = useRef()
  const videoStageRef = useRef()
  const hasVideoPlayer = blocks?.has("video_player")
  const videoPlayer = blocks?.get("video_player")
  const videoStageOffset = hasVideoPlayer ? ["start end", "end end"] : ["start start", "end start"]

  const { scrollY } = useScroll({ target: ref, offset: ["start start", "end start"] })
  const { scrollYProgress: videoStageProgress } = useScroll({ target: videoStageRef, offset: videoStageOffset })

  const ttlGradient = title_gradient?.colors ? getGradient(title_gradient.colors) : null
  const chapoGradient = chapo_gradient?.colors ? getGradient(chapo_gradient.colors) : null

  const percv = useTransform(videoStageProgress, value => transform(value, [0, 1], [0, 5]))
  const perch = useTransform(videoStageProgress, value => transform(value, [0, 1], [0, 5]))
  const watermarkOpacity = useTransform(videoStageProgress, value => transform(value, [0, 0.2], [1, 0]))
  const inset = useMotionTemplate`inset(${percv}% ${perch}%)`
  const maskStyle = hasVideoPlayer ? { clipPath: inset } : undefined
  const watermarkStyle = hasVideoPlayer ? { opacity: watermarkOpacity } : undefined

  const styles = {
    ...(ttlGradient && { "--title-gradient": ttlGradient }),
    ...(chapoGradient && { "--chapo-gradient": chapoGradient }),
    "--row-h": sub_heading?.length ? "150vh" : "100vh",
    "--video-row-h": hasVideoPlayer ? "150vh" : "0",
  }

  return (
    <>
      <PageTitleBlock
        {...heading}
        videoCTA={video_cta?.[0]}
        prog={scrollY}
        theme={theme ? theme?.theme : "dark-theme"}
        use_os_logo={use_os_logo}
        animated_logo={animated_logo}
        logo_translation={logo_translation}
      />
      <Section ref={ref} className={`${className} ${theme}`} style={styles}>
        {video ? (
          <BackgroundMask style={maskStyle}>
            <Video autoPlay loop noButton {...video} sizes='100vw' thresholdValue='0.5' />
            {hasVideoPlayer ? <Overlay prog={scrollY} doubled={sub_heading?.length} contained /> : null}
          </BackgroundMask>
        ) : null}
        {sub_heading?.length ? (
          <SubHeading theme={theme ? theme?.theme : "dark-theme"} prog={scrollY} has_long_sub_heading={has_long_sub_heading} {...sub_heading[0].heading} />
        ) : null}
        {watermark ? <WatermarkOyster style={watermarkStyle} /> : null}
        {!hasVideoPlayer ? <Overlay prog={scrollY} doubled={sub_heading?.length} /> : null}
        {blocks?.has("cta_block") ? (
          <BlockCTA {...blocks?.get("cta_block")} prog={scrollY} doubled={sub_heading?.length} />
        ) : blocks?.has("skyline") ? (
          <Skyline
            theme={secundary_theme ? secundary_theme?.theme : "dark-theme"}
            {...blocks?.get("skyline")}
            isHeading={heading?.title?.length}
            prog={scrollY}
            doubled={sub_heading?.length}
          />
        ) : null}
        <VideoStage ref={videoStageRef}>
          {hasVideoPlayer ? (
            <LauncherLayer style={maskStyle}>
              <VideoLauncher video_player_cld={videoPlayer} />
            </LauncherLayer>
          ) : null}
        </VideoStage>
      </Section>
    </>
  )
}
